import React, { useMemo, useState } from 'react';
import { CheckCircle2Icon, Loader2Icon } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { getLeadSource, trackEvent } from '../../utils/tracking';
const SERVICES = ['Logística', 'Transporte', 'Facility Management', 'HSE', 'Control Tower', 'Tecnología', 'Sustentabilidad', 'Otro'];
type Status = 'idle' | 'loading' | 'success' | 'error';
const FIELDS = [{
  name: 'nombre',
  label: 'Nombre y apellido',
  type: 'text',
  required: true,
  autoComplete: 'name'
}, {
  name: 'empresa',
  label: 'Empresa',
  type: 'text',
  required: true,
  autoComplete: 'organization'
}, {
  name: 'cargo',
  label: 'Cargo',
  type: 'text',
  required: true,
  autoComplete: 'organization-title'
}, {
  name: 'email',
  label: 'Email corporativo',
  type: 'email',
  required: true,
  autoComplete: 'email'
}, {
  name: 'telefono',
  label: 'Teléfono',
  type: 'tel',
  required: true,
  autoComplete: 'tel'
}, {
  name: 'ubicacion',
  label: 'Ubicación',
  type: 'text',
  required: true,
  autoComplete: 'address-level2'
}] as const;
export function ContactForm() {
  const [params] = useSearchParams();
  const preselected = params.get('servicio');
  const [status, setStatus] = useState<Status>('idle');
  const [service, setService] = useState(preselected && SERVICES.includes(preselected) ? preselected : SERVICES[0]);
  const source = useMemo(() => getLeadSource(), []);
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!form.checkValidity()) {
      setStatus('error');
      return;
    }
    setStatus('loading');
    trackEvent('generate_lead', {
      form: 'contacto',
      servicio: service,
      origen_pagina: source.page,
      origen_referrer: source.referrer,
      campana: source.campaign,
      utm_source: source.source,
      utm_medium: source.medium
    });
    window.setTimeout(() => setStatus('success'), 700);
  };
  if (status === 'success') {
    return <div role="status" className="flex flex-col items-start border border-brand-200 bg-brand-50 p-8" data-id="element-86" data-mp="rbvqhznug0ah">
        <CheckCircle2Icon className="h-8 w-8 text-brand-600" aria-hidden="true" data-id="element-87" data-mp="melsvwqawibk" />
        <h3 className="mt-5 font-display text-2xl font-bold tracking-tight text-graphite-900" data-id="element-88" data-mp="105qzxjw00cvx">Consulta registrada</h3>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-graphite-500" data-id="element-89" data-mp="vd7czwbf85eg">
          Un especialista de Rabemar revisará su caso y se pondrá en contacto para coordinar el diagnóstico operativo.
          Servicio indicado: <span className="font-semibold text-graphite-800" data-id="element-90" data-mp="153k11yig2gpc">{service}</span>.
        </p>
        <button type="button" onClick={() => setStatus('idle')} className="mt-7 text-sm font-semibold text-brand-700 transition-colors duration-150 hover:text-brand-600" data-id="element-91" data-mp="1j31p2bnx1pxn">
          Enviar otra consulta
        </button>
      </div>;
  }
  return <form onSubmit={handleSubmit} noValidate className="border border-mist-200 bg-white p-6 md:p-8" aria-describedby="form-nota" data-id="element-92" data-mp="3guo0gp8z33lh">
      <div className="grid gap-5 sm:grid-cols-2" data-id="element-93" data-mp="36rs7czbkg14z">
        {FIELDS.map(field => <label key={field.name} className={field.name === 'ubicacion' ? 'sm:col-span-2' : ''} data-id="element-94" data-mp="3aq807jubw3qb">
            <span className="block text-xs font-semibold uppercase tracking-[0.12em] text-graphite-500" data-id="element-95" data-mp="2wzcrq8nqron9">
              {field.label}
              {field.required && <span className="text-accent-600" data-id="element-96" data-mp="2n15n8pan2z25"> *</span>}
            </span>
            <input name={field.name} type={field.type} required={field.required} autoComplete={field.autoComplete} className="mt-2 w-full border border-mist-300 bg-white px-4 py-3 text-sm text-graphite-900 transition-colors duration-150 placeholder:text-graphite-500/60 focus:border-brand-500 focus:outline-none" data-id="element-97" data-mp="2s0i9b7tos23o" />
          </label>)}

        <label className="sm:col-span-2" data-id="element-98" data-mp="h8qhxg30q92j">
          <span className="block text-xs font-semibold uppercase tracking-[0.12em] text-graphite-500" data-id="element-99" data-mp="h7p4j4yj5ahn">
            Servicio requerido<span className="text-accent-600" data-id="element-100" data-mp="28cimwc365kai"> *</span>
          </span>
          <select name="servicio" required value={service} onChange={e => setService(e.target.value)} className="mt-2 w-full border border-mist-300 bg-white px-4 py-3 text-sm text-graphite-900 focus:border-brand-500 focus:outline-none" data-id="element-101" data-mp="28dbkamg8mxfz">
            {SERVICES.map(s => <option key={s} value={s} data-id="element-102" data-mp="1plis2u8ztg9b">
                {s}
              </option>)}
          </select>
        </label>

        <label className="sm:col-span-2" data-id="element-103" data-mp="1knpom8z2r0cq">
          <span className="block text-xs font-semibold uppercase tracking-[0.12em] text-graphite-500" data-id="element-104" data-mp="1kkle6tv0n8dm">
            Descripción de la necesidad<span className="text-accent-600" data-id="element-105" data-mp="1bmj31lhkizcm"> *</span>
          </span>
          <textarea name="descripcion" required rows={5} placeholder="Qué necesita resolver, volumen aproximado, ubicaciones involucradas y plazo previsto." className="mt-2 w-full border border-mist-300 bg-white px-4 py-3 text-sm text-graphite-900 transition-colors duration-150 placeholder:text-graphite-500/60 focus:border-brand-500 focus:outline-none" data-id="element-106" data-mp="16n6gl1ulzuak" />
        </label>
      </div>

      {/* Trazabilidad comercial del formulario */}
      <input type="hidden" name="origen_pagina" value={source.page} data-id="element-107" data-mp="2zk0ky5e0f6ub" />
      <input type="hidden" name="origen_referrer" value={source.referrer} data-id="element-108" data-mp="2zhxnj0aj2bfk" />
      <input type="hidden" name="campana" value={source.campaign} data-id="element-109" data-mp="33mf7p0ksuqqv" />
      <input type="hidden" name="utm_source" value={source.source} data-id="element-110" data-mp="38k6whlq5dwqu" />
      <input type="hidden" name="utm_medium" value={source.medium} data-id="element-111" data-mp="38ivt3n0m6r09" />

      {status === 'error' && <p role="alert" className="mt-6 border border-accent-300 bg-accent-300/15 p-4 text-sm text-accent-600" data-id="element-112" data-mp="2fw1bqyz3g7r0">
          Falta completar algún campo obligatorio o el formato del email no es válido. Revise los datos e intente
          nuevamente.
        </p>}

      <div className="mt-8 flex flex-wrap items-center gap-4" data-id="element-113" data-mp="3o4gwcpmu6qva">
        <button type="submit" disabled={status === 'loading'} className="inline-flex items-center gap-2 bg-brand-700 px-7 py-4 text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors duration-150 ease-smooth hover:bg-brand-600 disabled:opacity-70" data-id="element-114" data-mp="3j7qjeb6bwbe3">
          {status === 'loading' && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-115" data-mp="357phcsebuxx1" />}
          {status === 'loading' ? 'Enviando' : 'Solicitar diagnóstico'}
        </button>
        <p id="form-nota" className="text-xs leading-relaxed text-graphite-500" data-id="element-116" data-mp="3e846k2uewx5t">
          El formulario registra origen, página, campaña y servicio solicitado para el seguimiento comercial.
        </p>
      </div>
    </form>;
}